import 'core-js/actual';
import 'intl-pluralrules';
import 'whatwg-fetch';

import { ApolloProvider } from '@apollo/client';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';

import '../setup';

import { Locale, MailOptOutType, SportType } from '../__generated__/graphql';

import { ClaimLayout } from './Claim/ClaimLayout';
import { PortalLayout } from './Portal/PortalLayout';
import { Routes } from './Common/useNavigation';
import { TrackingProvider } from '../common/Tracking';
import { UserProvider, getLoginUrl } from '../common/Auth/useAuth';
import { WebsiteLayout } from './Website/WebsiteLayout';
import { client, setHeaders } from '../api/apollo';
import { guessLocale } from '../common/helpers';
import { setupTranslations, supportedLocales } from '../i18n';
import AccountPage from './Portal/AccountPage';
import CalendarPage from './Website/CalendarPage';
import ClaimRegistrationPage from './Claim/ClaimRegistrationPage';
import ClaimRegistrationsPage from './Claim/ClaimRegistrationsPage';
import ClaimSuccessPage from './Claim/ClaimSuccessPage';
import CookieConsentProvider from '../common/Common/CookieConsentProvider';
import Dashboard from './Dashboard/Dashboard';
import GlobalStyle from '../common/GlobalStyle';
import GuestLayout from '../common/Common/GuestLayout';
import HomePage from './Website/HomePage';
import LoginPage from './Login/LoginPage';
import NotFoundPage from './Website/NotFoundPage';
import NotificationProvider from '../common/Notifications/NotificationProvider';
import ParticipantProvider from './Dashboard/ParticipantProvider';
import PortalPage from './Portal/PortalPage';
import RollbarProvider from '../common/Common/RollbarProvider';
import ThemeProvider from '../common/Common/ThemeProvider';
import UI from '../common/UI';
import UnsubscribePage from './Unsubscribe/UnsubscribePage';

setupTranslations({
  namespaces: ['participant', 'frontend', 'common'],
});

setHeaders((headers) => ({
  ...headers,
  'Atleta-Guard': 'Participant',
}));

const themeOptions = { scrollOffset: { sm: 48, md: 48 } };

const App = () => (
  <RollbarProvider>
    <ThemeProvider options={themeOptions}>
      <GlobalStyle />
      <Suspense fallback={<UI.PageLoader />}>
        <CookieConsentProvider>
          <ApolloProvider client={client}>
            <BrowserRouter>
              <TrackingProvider>
                <UserProvider>
                  <NotificationProvider>
                    <Switch>
                      <Route path={Routes.Login}>
                        <GuestLayout>
                          <LoginPage />
                        </GuestLayout>
                      </Route>

                      <Route
                        path={Routes.Unsubscribe}
                        render={({ match: { params: { email, token, type } } }) => (
                          <GuestLayout>
                            <UnsubscribePage
                              email={email}
                              token={token}
                              type={type as MailOptOutType}
                            />
                          </GuestLayout>
                        )}
                      />

                      <Route
                        path="/p/:projectId/claim/:participantId?"
                        render={({ match: { params: { projectId, participantId } } }) => (
                          <ParticipantProvider projectId={projectId} participantId={participantId}>
                            <ClaimLayout>
                              <Switch>
                                <Route
                                  path={Routes.ClaimSuccess}
                                  render={({ match: { params: { eventId, participantId } } }) => (
                                    <ClaimSuccessPage
                                      participantId={participantId}
                                      eventId={eventId}
                                    />
                                  )}
                                />

                                <Route
                                  path={Routes.ClaimRegistration}
                                  render={({ match: { params: { registrationId, claimToken, eventId } } }) => (
                                    <ClaimRegistrationPage
                                      eventId={eventId}
                                      registrationId={registrationId}
                                      claimToken={claimToken}
                                    />
                                  )}
                                />

                                <Route
                                  path={Routes.ClaimRegistrations}
                                  render={({ match: { params: { eventId, participantId, claimToken } } }) => (
                                    <ClaimRegistrationsPage
                                      projectId={projectId}
                                      eventId={eventId}
                                      participantId={participantId}
                                      claimToken={claimToken}
                                    />
                                  )}
                                />
                              </Switch>
                            </ClaimLayout>
                          </ParticipantProvider>
                        )}
                      />

                      {/* Redirect legacy login route /p/:projectId/login */}
                      <Redirect from="/p/:projectId([A-Za-z0-9]{4})/login" to={getLoginUrl()} />

                      <Route
                        // Regex to make sure that the legacy route /p/login/:projectId is not matched.
                        path="/p/:projectId([A-Za-z0-9]{4})/:participantId"
                        render={({ match: { params: { projectId, participantId } } }) => (
                          <ParticipantProvider projectId={projectId} participantId={participantId}>
                            <Dashboard />
                          </ParticipantProvider>
                        )}
                      />

                      {/* Redirect rest to portal (including legacy URLs like /p/TEST and /p/login/TEST) */}
                      <Redirect from="/p/:projectId" to="/me" />

                      <Route path={Routes.Portal}>
                        <PortalLayout>
                          <Switch>
                            <Route path={Routes.Account}>
                              <AccountPage />
                            </Route>
                            <Route>
                              <PortalPage />
                            </Route>
                          </Switch>
                        </PortalLayout>
                      </Route>

                      <Route path={supportedLocales.current.map((locale) => `/${locale}`)}>
                        <WebsiteLayout>
                          <Switch>
                            <Route
                              path={Routes.Calendar}
                              render={({ match: { params: { sportType } } }) => (
                                <CalendarPage sportType={sportType as SportType} />
                              )}
                            />

                            <Route path="/:locale/events">
                              {({ match, location }) => (
                                <Redirect
                                  to={{
                                    pathname: `/${match.params.locale}/calendar`,
                                    search: location.search,
                                  }}
                                />
                              )}
                            </Route>

                            <Route path={Routes.Home} exact>
                              <HomePage />
                            </Route>

                            <Route>
                              <NotFoundPage />
                            </Route>
                          </Switch>
                        </WebsiteLayout>
                      </Route>

                      <Route path="/events">
                        {({ location }) => (
                          <Redirect
                            to={{
                              pathname: `/${guessLocale(Locale.en)}/calendar`,
                              search: location.search,
                            }}
                          />
                        )}
                      </Route>

                      <Redirect from="/:path?" to={`/${guessLocale(Locale.en)}/:path?`} />
                    </Switch>
                  </NotificationProvider>
                </UserProvider>
              </TrackingProvider>
            </BrowserRouter>
          </ApolloProvider>
        </CookieConsentProvider>
      </Suspense>
    </ThemeProvider>
  </RollbarProvider>
);

const container = document.getElementById('app');
const root = createRoot(container);
root.render(<App />);
